/**
 * What a caller may pre-fill on the new-session form, passed through
 * react-router navigation state. Every field is optional — anything omitted
 * falls back to the working-hours default.
 */
export interface NewSessionPrefill {
  /** YYYY-MM-DD, or a Date (legacy callers). The day the session starts on. */
  selectedDate?: string | Date;
  /** HH:mm, 24-hour. */
  startTime?: string;
  /** Minutes. */
  duration?: number;
}

/** Reads the prefill out of react-router's location state; null when absent. */
export const readNewSessionPrefill = (state: unknown): NewSessionPrefill | null => {
  if (!state || typeof state !== "object") return null;
  const { selectedDate, startTime, duration } = state as NewSessionPrefill;
  if (selectedDate === undefined && startTime === undefined && duration === undefined) {
    return null;
  }
  return { selectedDate, startTime, duration };
};

/** The prefilled date as a local Date, or null when none was passed / it is unparseable. */
export const prefillDate = (prefill: NewSessionPrefill | null): Date | null => {
  if (!prefill?.selectedDate) return null;
  if (prefill.selectedDate instanceof Date) {
    return Number.isNaN(prefill.selectedDate.getTime()) ? null : prefill.selectedDate;
  }
  // YYYY-MM-DD is parsed as local midnight, never UTC — a session's date must
  // not shift a day for instructors west of Greenwich.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(prefill.selectedDate);
  if (!match) return null;
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
